import { getProject, useUpdateProject } from '@client/store/project';
import { enqueueSnackbar } from 'notistack';
import amplitudeLog from '@client/amplitude';
import {
  AMP_PROJECT_OPEN_SETTINGS,
  AMP_PROJECT_UPDATE_PRODUCTS,
} from '@shared/constants';
import { useForceUpdateProductsInProjectCallback } from '@features/products/use-products.hooks';
import React, { useCallback, useMemo, useState } from 'react';
import { ProjectKebabMenu } from '@features/projects/lists/ProjectListItem/components/ProjectKebabMenu';
import { Box, SxProps, Tooltip } from '@mui/material';
import { KebabButtonProps } from '@ui/menus/KebabMenu/kebabmenu.model';

const ProjectSettings: React.FC<{
  hidden?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}> = ({ hidden, onOpen, onClose }) => {
  const project = getProject();

  const updateProject = useUpdateProject();
  const updateProductsInProjectCallback =
    useForceUpdateProductsInProjectCallback();

  const [kebabMenuAnchor, setKebabMenuAnchor] = useState<Element>();

  const updateProducts = useCallback(async () => {
    const updatedProject = updateProductsInProjectCallback(project);

    if (updatedProject !== project) {
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      await updateProject({
        kind: 'snapshot',
        project: updatedProject,
        successMessage: `Products updated in "${project.name}"`,
        errorMessage: `Failed to update products in "${project.name}"`,
        logError: true,
      });
      amplitudeLog(AMP_PROJECT_UPDATE_PRODUCTS, {
        ProjectId: project.id,
      });
    } else {
      enqueueSnackbar('Products are already up to date', {
        variant: 'info',
      });
    }
    setKebabMenuAnchor(undefined);
  }, [project, updateProductsInProjectCallback, updateProject]);

  const openKebabMenu = useCallback(
    (anchor?: Element) => {
      setKebabMenuAnchor(anchor);
      onOpen?.();
      amplitudeLog(AMP_PROJECT_OPEN_SETTINGS);
    },
    [onOpen],
  );

  const closeKebabMenu = useCallback(() => {
    setKebabMenuAnchor(undefined);
    onClose?.();
  }, [onClose]);

  const buttonProps = useMemo<Partial<KebabButtonProps>>(
    () => ({
      verticalIcon: true,
      'aria-label': 'Project settings',
      disableTouchRipple: true,
      sx: {
        padding: 0,
        fontSize: 12,
        opacity: 0.4,
        color: 'inherit',
        '&:hover': {
          opacity: 1,
          color: '#000',
        },
      },
    }),
    [],
  );

  if (!project.id) return null;

  return (
    <Tooltip title="Project settings" placement="bottom">
      <Box sx={hidden ? hiddenSx : visibleSx}>
        <ProjectKebabMenu
          projectId={project.id}
          projectName={project.name}
          locked={!!project.locked}
          archived={!!project.archived}
          anchor={kebabMenuAnchor}
          buttonProps={buttonProps}
          sx={containerSx}
          onOpen={openKebabMenu}
          onClose={closeKebabMenu}
          updateProducts={updateProducts}
        />
      </Box>
    </Tooltip>
  );
};

const containerSx: SxProps = { margin: 0 };

const hiddenSx: SxProps = {
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 0.15s',
};

const visibleSx: SxProps = {
  opacity: 1,
  pointerEvents: 'auto',
  // Let the project GFA leave the layout before revealing the kebab button,
  // so it never flashes briefly at the GFA's old right edge.
  transition: 'opacity 0.15s 0.05s',
};

export default ProjectSettings;
